import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { COLOR_SYSTEMS, NATURAL_MINERALS, TINTED_COLORS } from '../config/products';
import { MINERAL_GALLERY, NATURAL_TEXTURES } from '../config/images';
import { cn } from '../lib/utils';
import { Check, Palette, Sparkles, X, ZoomIn, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

// Map color IDs to texture images
const getTextureImage = (colorId) => {
  const textureMap = {
    'MM-N001': NATURAL_TEXTURES.betonite,
    'MM-N002': NATURAL_TEXTURES.betonda,
    'MM-N003': NATURAL_TEXTURES.mika,
    'MM-N004': NATURAL_TEXTURES.titanium,
    'MM-N005': NATURAL_TEXTURES.clay,
    'MM-N006': NATURAL_TEXTURES.yatir,
  };
  return textureMap[colorId];
};

// Color Detail Modal - Shows enlarged color with full details
const ColorDetailModal = ({ isOpen, onClose, color, isRTL, onSelect }) => {
  if (!isOpen || !color) return null;
  const textureImage = getTextureImage(color.code);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="relative max-w-sm w-full bg-card rounded-3xl overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 end-4 z-10 w-10 h-10 rounded-full bg-black/30 text-white flex items-center justify-center hover:bg-black/50 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Large Color Swatch - Use texture image if available */}
        <div className="aspect-square relative overflow-hidden">
          {textureImage ? (
            <img 
              src={textureImage} 
              alt={color.nameHe}
              className="w-full h-full object-cover scale-125"
            />
          ) : (
            <div
              className="w-full h-full"
              style={{ background: color.gradient || color.hex }}
            >
              {/* Texture overlay for natural minerals */}
              {color.texture && (
                <div 
                  className="absolute inset-0 opacity-40"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                  }}
                />
              )}
            </div>
          )}
          
          {/* Color Code Badge */}
          <div className="absolute top-4 start-4">
            <span className="px-3 py-1.5 rounded-full bg-black/40 text-white text-sm font-mono backdrop-blur-sm">
              {color.code}
            </span>
          </div>
        </div>
        
        {/* Color Info */}
        <div className="p-6 space-y-4">
          <div>
            <h3 className="text-2xl font-bold text-foreground">
              {isRTL ? color.nameHe : color.nameEn}
            </h3>
            <p className="text-muted-foreground mt-1">
              {isRTL ? color.descriptionHe : color.descriptionEn}
            </p>
          </div>
          
          {color.texture && (
            <p className="text-xs text-accent italic">
              {isRTL 
                ? 'צבע מגיע מהמינרל הטבעי – ללא פיגמנטים מלאכותיים'
                : 'Color derived from natural mineral – no synthetic pigments'
              }
            </p>
          )}
          
          <Button 
            onClick={() => {
              onSelect(color);
              onClose();
            }}
            className="w-full h-12 text-base"
          >
            {isRTL ? 'בחר גוון זה' : 'Select This Color'}
            <ChevronRight className="w-5 h-5 ms-2" />
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Gallery Modal Component
const GalleryModal = ({ isOpen, onClose, images, currentIndex, setCurrentIndex, isRTL }) => {
  if (!isOpen) return null;
  
  const imageKeys = Object.keys(images);
  const currentKey = imageKeys[currentIndex];
  const mineralNames = {
    betonite: { he: 'בטונייט', en: 'Betonite', code: 'MM-N001' },
    betonda: { he: 'בטונדה', en: 'Betonda', code: 'MM-N002' },
    mika: { he: 'מיקה', en: 'Mika', code: 'MM-N003' },
    titanium: { he: 'טיטניום', en: 'Titanium', code: 'MM-N004' },
    clay: { he: 'חרסית ממשית', en: 'Clay (Terracotta)', code: 'MM-N005' },
    yatir: { he: 'יתיר', en: 'Yatir', code: 'MM-N006' },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative max-w-3xl w-full bg-card rounded-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 end-4 z-10 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="aspect-square relative">
          <img
            src={images[currentKey]}
            alt={mineralNames[currentKey]?.[isRTL ? 'he' : 'en'] || currentKey}
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-6">
            <span className="text-white/60 text-sm font-mono">
              {mineralNames[currentKey]?.code}
            </span>
            <p className="text-white text-2xl font-bold mt-1">
              {mineralNames[currentKey]?.[isRTL ? 'he' : 'en'] || currentKey}
            </p>
            <p className="text-white/70 text-sm">
              {isRTL ? 'ציפוי מינרלי טבעי' : 'Natural Mineral Coating'}
            </p>
          </div>
        </div>
        
        {/* Thumbnail navigation */}
        <div className="flex gap-2 p-4 bg-card overflow-x-auto">
          {imageKeys.map((key, idx) => (
            <button
              key={key}
              onClick={() => setCurrentIndex(idx)}
              className={cn(
                'w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all',
                idx === currentIndex ? 'border-accent' : 'border-transparent opacity-60 hover:opacity-100'
              )}
            >
              <img src={images[key]} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Color System Toggle with Info Button
const SystemToggle = ({ activeSystem, onToggle, isRTL, onInfoClick }) => {
  return (
    <div className="space-y-3 mb-6">
      {/* Toggle buttons with enhanced styling */}
      <div className="flex gap-2">
        {Object.values(COLOR_SYSTEMS).map((system) => (
          <button
            key={system.id}
            onClick={() => onToggle(system.id)}
            className={cn(
              'flex-1 px-3 py-4 rounded-xl text-sm font-medium transition-all border-2',
              activeSystem === system.id
                ? system.id === 'natural'
                  ? 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-300 dark:border-amber-700 shadow-lg'
                  : 'bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border-violet-300 dark:border-violet-700 shadow-lg'
                : 'bg-secondary/30 border-transparent hover:bg-secondary/50'
            )}
          >
            <div className="flex flex-col items-center gap-1.5">
              {system.id === 'natural' ? (
                <Sparkles className={cn('w-5 h-5', activeSystem === system.id ? 'text-amber-600' : 'text-muted-foreground')} />
              ) : (
                <Palette className={cn('w-5 h-5', activeSystem === system.id ? 'text-violet-600' : 'text-muted-foreground')} />
              )}
              <span className={cn(
                'text-xs sm:text-sm font-semibold',
                activeSystem === system.id ? 'text-foreground' : 'text-muted-foreground'
              )}>
                {isRTL ? system.nameHe : system.nameEn}
              </span>
              <span className={cn(
                'text-[10px]',
                activeSystem === system.id ? 'text-muted-foreground' : 'text-muted-foreground/60'
              )}>
                {system.id === 'natural' 
                  ? (isRTL ? '6 גוונים טבעיים' : '6 Natural Shades')
                  : (isRTL ? '21 גוונים צבועים' : '21 Tinted Shades')
                }
              </span>
            </div>
          </button>
        ))}
      </div>
      
      {/* Info Button */}
      <button
        onClick={onInfoClick}
        className="w-full flex items-center justify-center gap-2 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        <span className="w-4 h-4 rounded-full border border-current flex items-center justify-center text-[10px] font-bold">?</span>
        <span>{isRTL ? 'מה ההבדל בין המניפות?' : "What's the difference?"}</span>
      </button>
    </div>
  );
};

// Natural Mineral Color Card with texture - Opens detail modal on click
const NaturalColorCard = ({ color, isSelected, onSelect, onDetailOpen, isRTL, index }) => {
  const textureImage = getTextureImage(color.code);
  
  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={() => onDetailOpen(color)}
      className={cn(
        'relative p-3 rounded-2xl border-2 transition-all text-start group',
        isSelected
          ? 'border-accent shadow-lg bg-accent/5 ring-2 ring-accent/20'
          : 'border-border/40 hover:border-accent/40 bg-card'
      )}
    >
      {isSelected && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -top-2 -end-2 w-6 h-6 rounded-full bg-accent flex items-center justify-center shadow-md z-10"
        >
          <Check className="w-3.5 h-3.5 text-accent-foreground" />
        </motion.div>
      )}

      {/* Textured color swatch - Use real texture image */}
      <div className="w-full aspect-square rounded-xl mb-2 relative overflow-hidden">
        {textureImage ? (
          <img 
            src={textureImage} 
            alt={color.nameHe}
            className="w-full h-full object-cover scale-[1.8]"
          />
        ) : (
          <div
            className="w-full h-full"
            style={{ background: color.gradient }}
          >
            <div 
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
              }}
            />
          </div>
        )}
        
        {/* Zoom indicator */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
          <ZoomIn className="w-6 h-6 text-white" />
        </div>
      </div>

      <div className="space-y-0.5">
        <p className="text-xs text-muted-foreground font-mono">{color.code}</p>
        <h4 className="text-sm font-semibold text-foreground">
          {isRTL ? color.nameHe : color.nameEn}
        </h4>
      </div>
    </motion.button>
  );
};

// Tinted Color Card - Opens detail modal on click
const TintedColorCard = ({ color, isSelected, onSelect, onDetailOpen, isRTL, index }) => {
  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.02 }}
      onClick={() => onDetailOpen(color)}
      className={cn(
        'relative p-2 rounded-xl border-2 transition-all text-start group',
        isSelected
          ? 'border-accent shadow-md ring-2 ring-accent/20'
          : 'border-border/30 hover:border-accent/30'
      )}
    >
      {isSelected && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -top-1.5 -end-1.5 w-5 h-5 rounded-full bg-accent flex items-center justify-center shadow-sm z-10"
        >
          <Check className="w-3 h-3 text-accent-foreground" />
        </motion.div>
      )}

      <div
        className="w-full aspect-[4/3] rounded-lg mb-1.5 relative overflow-hidden group-hover:ring-2 group-hover:ring-accent/30 transition-all"
        style={{ backgroundColor: color.hex }}
      >
        {/* Zoom indicator */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/10">
          <ZoomIn className="w-4 h-4 text-white drop-shadow" />
        </div>
      </div>
      
      <p className="text-[10px] text-muted-foreground font-mono">{color.code}</p>
      <p className="text-xs font-medium text-foreground truncate">
        {isRTL ? color.nameHe : color.nameEn}
      </p>
    </motion.button>
  );
};

// Info Modal - Explains the difference between color systems
const ColorSystemInfoModal = ({ isOpen, onClose, isRTL }) => {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative max-w-md w-full bg-card rounded-2xl p-6 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 end-4 w-8 h-8 rounded-full bg-secondary flex items-center justify-center hover:bg-secondary/80 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <h3 className="text-lg font-bold text-foreground mb-4 text-center">
          {isRTL ? 'מה ההבדל?' : "What's the Difference?"}
        </h3>

        <div className="space-y-4">
          {/* Natural Minerals */}
          <div className="p-4 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-amber-600" />
              <h4 className="font-semibold text-foreground">
                {isRTL ? 'ציפויים מינרליים טבעיים' : 'Natural Mineral Coatings'}
              </h4>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {isRTL 
                ? 'גוונים המבוססים על מינרלים טבעיים בלבד. מראה אותנטי עם טקסטורה ייחודית. מתאים למי שמחפש מראה טבעי ואורגני.'
                : 'Colors based on natural minerals only. Authentic look with unique texture. Perfect for those seeking a natural, organic appearance.'}
            </p>
          </div>

          {/* Tinted Colors */}
          <div className="p-4 rounded-xl bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border border-violet-200 dark:border-violet-800">
            <div className="flex items-center gap-2 mb-2">
              <Palette className="w-5 h-5 text-violet-600" />
              <h4 className="font-semibold text-foreground">
                {isRTL ? 'צבעים מגוונים' : 'Tinted Colors'}
              </h4>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {isRTL 
                ? 'מגוון רחב של 21 גוונים צבועים. מאפשר התאמה מדויקת לכל סגנון עיצובי. מתאים למי שמחפש צבע ספציפי.'
                : 'Wide range of 21 tinted shades. Allows precise matching to any design style. Perfect for those seeking a specific color.'}
            </p>
          </div>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-4">
          {isRTL 
            ? '💡 לא בטוחים? הזמינו מארז דוגמיות להתנסות'
            : '💡 Not sure? Order a sample pack to try'}
        </p>
      </motion.div>
    </motion.div>
  );
};

export const ColorSelector = ({ selectedColor, onSelect }) => {
  const { isRTL } = useLanguage();
  const [activeSystem, setActiveSystem] = useState('natural');
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [detailColor, setDetailColor] = useState(null);
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  const naturalColors = Object.values(NATURAL_MINERALS);
  const tintedColors = Object.values(TINTED_COLORS);
  
  const openGallery = () => {
    setGalleryIndex(0);
    setGalleryOpen(true);
  };

  const handleSelect = (color) => {
    onSelect({
      ...color,
      system: activeSystem,
    });
  };

  // Open detail modal - does NOT auto-advance
  const openDetailModal = (color) => {
    setDetailColor(color);
  };

  return (
    <div className="space-y-4">
      {/* System Toggle with Info */}
      <SystemToggle 
        activeSystem={activeSystem} 
        onToggle={setActiveSystem}
        isRTL={isRTL}
        onInfoClick={() => setInfoModalOpen(true)}
      />

      {/* System description */}
      <motion.div
        key={activeSystem}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center pb-2"
      >
        <p className="text-sm text-muted-foreground">
          {activeSystem === 'natural' 
            ? (isRTL ? COLOR_SYSTEMS.natural.descriptionHe : COLOR_SYSTEMS.natural.descriptionEn)
            : (isRTL ? COLOR_SYSTEMS.tinted.descriptionHe : COLOR_SYSTEMS.tinted.descriptionEn)
          }
        </p>
        
        {activeSystem === 'natural' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={openGallery}
            className="mt-2 text-accent"
          >
            <ZoomIn className="w-4 h-4 me-2" />
            {isRTL ? 'צפה בגלריה' : 'View Gallery'}
          </Button>
        )}
      </motion.div>

      {/* Color Grid */}
      <AnimatePresence mode="wait">
        {activeSystem === 'natural' ? (
          <motion.div
            key="natural"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
          >
            {naturalColors.map((color, index) => (
              <NaturalColorCard
                key={color.id}
                color={color}
                isSelected={selectedColor?.id === color.id}
                onSelect={handleSelect}
                onDetailOpen={openDetailModal}
                isRTL={isRTL}
                index={index}
              />
            ))}
          </motion.div>
        ) : (
          <motion.div
            key="tinted"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-2"
          >
            {tintedColors.map((color, index) => (
              <TintedColorCard
                key={color.id}
                color={color}
                isSelected={selectedColor?.id === color.id}
                onSelect={handleSelect}
                onDetailOpen={openDetailModal}
                isRTL={isRTL}
                index={index}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Selected Color Preview */}
      {selectedColor && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-4 flex items-center gap-4 mt-6"
        >
          <div
            className="w-14 h-14 rounded-xl flex-shrink-0"
            style={{ 
              background: selectedColor.gradient || selectedColor.hex,
            }}
          />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground mb-0.5">
              {isRTL ? 'נבחר' : 'Selected'} • {selectedColor.code}
            </p>
            <h4 className="font-semibold text-foreground truncate">
              {isRTL ? selectedColor.nameHe : selectedColor.nameEn}
            </h4>
            <p className="text-xs text-muted-foreground">
              {selectedColor.system === 'natural' 
                ? (isRTL ? 'ציפוי מינרלי טבעי' : 'Natural Mineral Coating')
                : (isRTL ? 'צבע מינרלי' : 'Tinted Mineral Color')
              }
            </p>
          </div>
        </motion.div>
      )}

      {/* Color Detail Modal */}
      <AnimatePresence>
        {detailColor && (
          <ColorDetailModal
            isOpen={!!detailColor}
            onClose={() => setDetailColor(null)}
            color={detailColor}
            isRTL={isRTL}
            onSelect={handleSelect}
          />
        )}
      </AnimatePresence>

      {/* Info Modal */}
      <AnimatePresence>
        {infoModalOpen && (
          <ColorSystemInfoModal
            isOpen={infoModalOpen}
            onClose={() => setInfoModalOpen(false)}
            isRTL={isRTL}
          />
        )}
      </AnimatePresence>

      {/* Gallery Modal */}
      <AnimatePresence>
        {galleryOpen && (
          <GalleryModal
            isOpen={galleryOpen}
            onClose={() => setGalleryOpen(false)}
            images={MINERAL_GALLERY}
            currentIndex={galleryIndex}
            setCurrentIndex={setGalleryIndex}
            isRTL={isRTL}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
